import { useQuery } from '@tanstack/react-query'
import type { UseQueryResult } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { QueryState } from '@/shared/ui/QueryState'
import { DefaultLayout } from '@/shared/ui/DefaultLayout'
import { ChevronRightIcon } from '@/shared/ui/icons/ChevronRightIcon'
import { boardTabOf, type BoardTab } from '@/public/features/board/constants/boardTab'
import { fetchGhostPostBySlug, type GhostPost } from '@/shared/lib/ghost'

export function BoardDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const post = useQuery({
    queryKey: ['ghost-post', slug],
    queryFn: () => fetchGhostPostBySlug(slug ?? ''),
    enabled: Boolean(slug),
  })

  const tab = boardTabOf(post.data?.tags?.[0]?.slug ?? null)

  return (
    <>
      <BoardDetailMobile query={post} tab={tab} />
      <BoardDetailPc query={post} tab={tab} />
    </>
  )
}

// 모바일은 헤더가 제목과 뒤로가기를 대신 그린다 — 본문에 따로 「목록」 링크를 두지 않는다.
function BoardDetailMobile(props: { query: UseQueryResult<GhostPost | null>; tab: BoardTab }) {
  const p = props.query.data

  return (
    <DefaultLayout mobileTitle={props.tab.label} className="pc:hidden flex min-h-[100dvh] flex-col">
      <QueryState isPending={props.query.isPending} error={props.query.error}>
        {p ? (
          <article className="layout-body flex flex-col gap-6 p-4">
            <div className="flex flex-col gap-2">
              <h1 className="m-0 text-headline-xs leading-[1.7] font-bold tracking-[-0.1px] text-black-800">
                {p.title}
              </h1>
              <span className="text-body-sm leading-[1.2] text-black-100">
                {p.published_at?.slice(0, 10) ?? ''}
              </span>
            </div>

            <PostBody post={p} />
          </article>
        ) : null}
      </QueryState>
    </DefaultLayout>
  )
}

function BoardDetailPc(props: { query: UseQueryResult<GhostPost | null>; tab: BoardTab }) {
  const p = props.query.data

  return (
    <DefaultLayout tree="pc" className="[--body-w:1000px]">
      <QueryState isPending={props.query.isPending} error={props.query.error}>
        {p ? (
          <div className="layout-body pt-8 pb-[60px]">
            <nav className="flex items-center gap-2 text-body-sm font-medium text-black-100">
              <Link className="text-inherit no-underline" to={`/board?tag=${props.tab.tag}`}>
                {props.tab.label}
              </Link>
              <ChevronRightIcon size={16} />
              <span>{p.title}</span>
            </nav>

            <article className="mt-6 flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <h1 className="m-0 text-headline-sm font-bold tracking-[-0.02em] text-black-800">
                  {p.title}
                </h1>
                <span className="text-body-sm text-black-100">
                  {p.published_at?.slice(0, 10) ?? ''}
                </span>
              </div>

              <PostBody post={p} />
            </article>
          </div>
        ) : null}
      </QueryState>
    </DefaultLayout>
  )
}

function PostBody({ post }: { post: GhostPost }) {
  return (
    <>
      {post.feature_image !== null && (
        <img className="w-full rounded-[14px]" src={post.feature_image} alt="" loading="lazy" />
      )}

      {/* Ghost 가 주는 본문은 HTML 이다. 글은 우리 관리자만 쓰므로 그대로 그린다. */}
      <div
        className="text-body-md-reading text-black-800"
        dangerouslySetInnerHTML={{ __html: post.html ?? '' }}
      />
    </>
  )
}
